import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type {
  OpsTenantDetailDto,
  OpsTenantListDto,
  TeamInvitationDto,
  TenantActivityListDto,
} from '@lib/dto/team.dto'
import type { ApiKeyDto, ApiKeyListDto } from '@lib/dto/tenant-settings.dto'
import type { InviteMemberInput } from '@lib/input/team.input'
import type { CreateTenantInput, TenantProfileInput } from '@lib/input/tenant.input'
import type { Uuid } from '@lib/validators/params'
import { opsTenantService } from '@/services/TeamService'
import { opsTenantKeys } from '@/hooks/queryKeys'

export function useOpsTenants(): UseQueryResult<OpsTenantListDto> {
  return useQuery({ queryKey: opsTenantKeys.list(), queryFn: () => opsTenantService.list() })
}

function requireTenantId(tenantId: Uuid | null): Uuid {
  if (tenantId === null) throw new Error('Tenant id is required')
  return tenantId
}

export function useOpsTenant(tenantId: Uuid | null): UseQueryResult<OpsTenantDetailDto> {
  return useQuery({
    queryKey: opsTenantKeys.detail(tenantId),
    queryFn: () => opsTenantService.get(requireTenantId(tenantId)),
    enabled: tenantId !== null,
  })
}

export function useOpsTenantActivity(tenantId: Uuid | null): UseQueryResult<TenantActivityListDto> {
  return useQuery({
    queryKey: opsTenantKeys.activity(tenantId),
    queryFn: () => opsTenantService.activity(requireTenantId(tenantId)),
    enabled: tenantId !== null,
  })
}

export function useOpsTenantKeys(tenantId: Uuid | null): UseQueryResult<ApiKeyListDto> {
  return useQuery({
    queryKey: opsTenantKeys.apiKeys(tenantId),
    queryFn: () => opsTenantService.listKeys(requireTenantId(tenantId)),
    enabled: tenantId !== null,
  })
}

export function useCreateTenant(): UseMutationResult<OpsTenantDetailDto, Error, CreateTenantInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input) => opsTenantService.create(input),
    onSettled: () => queryClient.invalidateQueries({ queryKey: opsTenantKeys.all }),
  })
}

/** Staff actions on one tenant. Each refreshes every ops tenant view when it settles. */
export function useOpsTenantActions(tenantId: Uuid | null): {
  update: UseMutationResult<OpsTenantDetailDto, Error, TenantProfileInput>
  suspend: UseMutationResult<OpsTenantDetailDto, Error, string>
  reactivate: UseMutationResult<OpsTenantDetailDto, Error, void>
  close: UseMutationResult<OpsTenantDetailDto, Error, string>
  reopen: UseMutationResult<OpsTenantDetailDto, Error, void>
  invite: UseMutationResult<TeamInvitationDto, Error, InviteMemberInput>
  resend: UseMutationResult<TeamInvitationDto, Error, Uuid>
  revoke: UseMutationResult<void, Error, Uuid>
  revokeKey: UseMutationResult<ApiKeyDto, Error, Uuid>
} {
  const queryClient = useQueryClient()
  const onSettled = (): Promise<void> => queryClient.invalidateQueries({ queryKey: opsTenantKeys.all })
  return {
    update: useMutation({
      mutationFn: (input) => opsTenantService.update(requireTenantId(tenantId), input),
      onSettled,
    }),
    suspend: useMutation({
      mutationFn: (reason) => opsTenantService.suspend(requireTenantId(tenantId), { reason }),
      onSettled,
    }),
    reactivate: useMutation({
      mutationFn: () => opsTenantService.reactivate(requireTenantId(tenantId)),
      onSettled,
    }),
    close: useMutation({
      mutationFn: (reason) => opsTenantService.close(requireTenantId(tenantId), { reason }),
      onSettled,
    }),
    reopen: useMutation({ mutationFn: () => opsTenantService.reopen(requireTenantId(tenantId)), onSettled }),
    invite: useMutation({
      mutationFn: (input) => opsTenantService.invite(requireTenantId(tenantId), input),
      onSettled,
    }),
    resend: useMutation({
      mutationFn: (id) => opsTenantService.resend(requireTenantId(tenantId), id),
      onSettled,
    }),
    revoke: useMutation({
      mutationFn: (id) => opsTenantService.revoke(requireTenantId(tenantId), id),
      onSettled,
    }),
    revokeKey: useMutation({
      mutationFn: (keyId) => opsTenantService.revokeKey(requireTenantId(tenantId), keyId),
      onSettled,
    }),
  }
}
